<template>
  <!-- Upload a logo, or let the AI find it on the company's own website and pick one (US 9370, US 9371). Saved straight away. -->
  <div class="v2-field" :qid="qid">
    <span class="v2-field__label">{{ $t('system_logo.label') }}</span>
    <b-alert v-if="error" show variant="danger" :qid="qid + '-error'">{{ error }}</b-alert>
    <div class="sx-logo">
      <span class="sx-logo__tile" :class="{ 'has-image': logo }">
        <img v-if="logo" :src="logo" alt="">
        <template v-else>{{ initials }}</template>
      </span>
      <div class="sx-logo__actions">
        <label class="v2-link sx-logo__upload" :qid="qid + '-upload'">
          <font-awesome-icon icon="cloud-upload-alt"/>{{ $t('system_logo.upload') }}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" class="d-none" @change="upload">
        </label>
        <button v-if="aiOn"
                type="button"
                class="v2-link sx-logo__ai"
                :disabled="finding"
                :qid="qid + '-find'"
                @click="findLogo">
          <font-awesome-icon :icon="finding ? 'spinner' : 'sparkles'" :class="{ 'fa-spin': finding }"/>{{ finding ? $t('system_logo.finding') : $t('system_logo.find') }}
        </button>
        <button v-if="logo" type="button" class="v2-link is-danger" :qid="qid + '-remove'" @click="removeLogo">
          <font-awesome-icon icon="times"/>{{ $t('system_logo.remove') }}
        </button>
      </div>
    </div>
    <div v-if="candidates" class="sx-logo__found">
      <small class="v2-field__note mt-0">{{ candidates.length ? $t('system_logo.pick', { vendor: vendor || name }) : $t('system_logo.none') }}</small>
      <div class="sx-logo__grid">
        <button v-for="(candidate, index) in candidates"
                :key="index"
                type="button"
                class="sx-logo__candidate"
                :title="candidate.source"
                :qid="qid + '-candidate-' + index"
                @click="pickLogo(candidate)">
          <img :src="candidate.image" alt="">
          <small>{{ host(candidate.source) }}</small>
        </button>
      </div>
    </div>
    <small class="v2-field__note">{{ $t('system_logo.hint') }}</small>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  export default {
    name: 'V2LogoPicker',
    props: {
      // e.g. "vendors/microsoft/logo"; the finder is <endpoint>/find
      endpoint: { type: String, required: true },
      // The logo shown now (data URI or url)
      value: { type: String, default: null },
      name: { type: String, default: '' },
      qid: { type: String, default: 'logo-picker' }
    },
    data() {
      return {
        logo: this.value,
        candidates: null,
        vendor: null,
        finding: false,
        error: ''
      }
    },
    watch: {
      value(value) {
        this.logo = value;
      }
    },
    computed: {
      // Finding a logo asks the AI, so it is part of the AI module
      aiOn() {
        return !!(this.$store.state.client && this.$store.state.client.aiModule);
      },
      initials() {
        let words = (this.name || '').trim().split(/\s+/).filter(Boolean);
        if (words.length > 1) {
          return (words[0][0] + words[1][0]).toUpperCase();
        }
        return (words[0] || '').slice(0, 3);
      }
    },
    methods: {
      host(url) {
        try {
          return new URL(url).hostname.replace(/^www\./, '');
        } catch (e) {
          return url;
        }
      },
      // Tells the page about the new logo; the response can hold a small and a large version
      saved(data, message) {
        this.logo = data.logo_large || data.logo || null;
        this.candidates = null;
        this.error = '';
        this.$toastr('success', this.$t(message));
        this.$emit('saved', data);
      },
      upload(event) {
        let self = this;
        let file = event.target.files[0];
        event.target.value = '';
        if (!file) {
          return;
        }
        let data = new FormData();
        data.append('file', file);
        piincHttp.post(this.endpoint, data, { headers: { 'Content-Type': 'multipart/form-data' } }).then(function(response) {
          self.saved(response.data, 'system_logo.saved');
        }, function(error) {
          self.error = (error.data && error.data.message) || self.$t('system_logo.invalid');
        });
      },
      // The AI names the company's website; its icons are offered to pick from, nothing is saved yet
      findLogo() {
        let self = this;
        this.finding = true;
        this.error = '';
        piincHttp.post(this.endpoint + '/find').then(function(response) {
          self.vendor = response.data.vendor;
          return Promise.all(response.data.candidates.map(candidate => self.visible(candidate.image)))
            .then(function(visible) {
              self.finding = false;
              self.candidates = response.data.candidates.filter((candidate, index) => visible[index]);
            });
        }, function(error) {
          self.finding = false;
          self.error = (error.data && error.data.message) || self.$t('system_logo.ai_failed');
        });
      },
      // Draws the image to check it shows something: some sites serve blank or transparent icons
      visible(src) {
        return new Promise(function(resolve) {
          let image = new Image();
          image.onload = function() {
            try {
              let size = 24;
              let canvas = document.createElement('canvas');
              canvas.width = size;
              canvas.height = size;
              let context = canvas.getContext('2d');
              context.drawImage(image, 0, 0, size, size);
              let pixels = context.getImageData(0, 0, size, size).data;
              let colours = {};
              for (let i = 0; i < pixels.length; i += 4) {
                if (pixels[i + 3] > 40) {
                  colours[(pixels[i] >> 5) + '-' + (pixels[i + 1] >> 5) + '-' + (pixels[i + 2] >> 5)] = true;
                }
              }
              resolve(Object.keys(colours).length > 1);
            } catch (e) {
              resolve(true);
            }
          };
          image.onerror = function() {
            resolve(false);
          };
          image.src = src;
        });
      },
      pickLogo(candidate) {
        let self = this;
        piincHttp.post(this.endpoint, { image: candidate.image, source: candidate.source }).then(function(response) {
          self.saved(response.data, 'system_logo.saved');
        }, function(error) {
          self.error = (error.data && error.data.message) || self.$t('system_logo.invalid');
        });
      },
      removeLogo() {
        let self = this;
        piincHttp.delete(this.endpoint).then(function() {
          self.saved({ logo: null, logo_large: null }, 'system_logo.removed');
        }, function() {});
      }
    }
  }
</script>
